import React, { useEffect, useMemo, useState } from 'react';
import { Row } from 'react-bootstrap';
import cx from 'classnames';
import { filter, find, flatMap, forEach, get, includes, isEmpty, map, omit, pickBy, round, size, snakeCase, some, times, toNumber, trim } from 'lodash';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import withErrorBoundary from 'src/components/errorBoundary/withErrorBoundary';
import { SearchBar } from 'src/components/input';
import SwitchButton from 'src/components/switchButton';
import { Deal, DealsRequestPayload, SortOrder, } from 'src/types/dealInsights';
import TabPanel from '../components/tabPanel';
import TableHeader from './components/tableHeader';
import TableCell from './components/tableCell';
import Actions from 'src/components/actionbar';
import InfiniteScroll from 'src/components/infiniteScroll';
import PlaceholderCell from './components/placeholderCell';
import { useDealFilterOptions, useDealInsightsSummary, useDealsList } from 'src/query/dealInsights/dealInsights';
import FiltersSlider from './components/filtersSlider';
import styles from './styles.module.scss';
import moment from 'moment';
import DealActivity from '../components/dealActivity';
import { useSelector } from 'react-redux';
import { BulkFiltersDefaultValues, inSightsLabel, dealInsightsTabs, dealTableHeaders, dateFilterFormatter, bulkFiltersMultiSelectKeys } from 'src/utils/dealInsights.helper';
import buildUrl from 'build-url-ts';
import { formatCurrencyToUSD } from 'src/utils/helper';
import DealAgent from '../components/dealAgent';

const intialRequestFilters: DealsRequestPayload = {
    page: 1,
    size: 20,
}

const DefaultUserAvatar = '/images/default_user_profile_background.png';
const emptyDeals = '/images/dealInsights/emptyDeals.svg';
const emptyInsights = '/images/dealInsights/noStatis.svg';

const NoResultsFound = ({ msg,src }: { msg: string,src: string }) => (
    <div className={styles.emptyState}>
        <img src={src} alt="No results" className={styles.emptyStateImg} />
        <p className={styles.emptyStateMsg}>{msg}</p>
    </div>
);

const RowPlaceholder = () => {
    return (<Row className={styles.tableRow}>
        {map(dealTableHeaders, (header) => (
            <PlaceholderCell
                key={header.label}
                col={header.width}
                type={header.value}
            />
        ))}
    </Row>)
};

const InsightPlaceholder = () => {
    return (<div className={styles.insightPlacholder}>
         <div className={cx(styles.placeholder, styles.header)}>
               <span className={cx(styles.line, styles.block)} />
         </div>
         <div className={cx(styles.placeholder,  styles.desc)}>
               <span className={cx(styles.line, styles.block)} />
         </div>
    </div>)
};

interface FilterPillsProps {
    filters: any;
    onClear?: () => void;
    onRemove?: (key: string, value?: any) => void;
}

const FilterPills = ({ filters, onClear, onRemove }: FilterPillsProps) => {

    const renderPill = (label: string, key: string, value?: string) => (
        <div className={styles.pill}>
            { key === 'ownerExternalId' && <img src={DefaultUserAvatar} className={styles.avatar} />}
            <p>{label}</p>
            {onRemove && (<i className={'paperflite-close'} onClick={() => onRemove(key, value)}></i>)}
        </div>
    );

    return (
        <div className={styles.filtersPillsContainer}>
            <div className={styles.filters}>
                {/* PIPELINE */}
                {filters.pipelineExternalId && (
                    renderPill(filters.pipelineExternalId.label, 'pipelineExternalId')
                )}

                {/* STAGE */}
                {filters.stageExternalId && size(filters.stageExternalId) > 0 && map(filters.stageExternalId, (stage) =>
                    renderPill(
                        stage.label,
                        'stageExternalId',
                        stage.externalId
                ))}

                {/* OWNER */}
                {filters.ownerExternalId && size(filters.ownerExternalId) > 0 && map(filters.ownerExternalId, (owner) =>
                    renderPill(
                        owner.name,
                        'ownerExternalId',
                        owner.externalId
                ))}
                
                {/* AMOUNT */}
                {(filters.minAmount || filters.maxAmount) &&
                    renderPill(
                        `$${filters.minAmount || 0} - $${filters.maxAmount || 0}`,
                        'amount'
                    )}


                {/* CLOSE DATE */}
                {(filters.closeDateFrom || filters.closeDateTo) &&
                    renderPill(
                        `${dateFilterFormatter(filters.closeDateFrom).format("DD MMM'YY") ?? ''} - ${dateFilterFormatter(filters.closeDateTo).format("DD MMM'YY")}`,
                        'closeDate'
                    )}

                {/* LAST ACTIVITY */}
                {(filters.lastActivityFrom || filters.lastActivityTo) &&
                    renderPill(
                        `${dateFilterFormatter(filters.lastActivityFrom).format("DD MMM'YY")} - ${dateFilterFormatter(filters.lastActivityTo).format("DD MMM'YY")}`,
                        'lastActivity'
                    )}

                {/* CREATED DATE */}
                {(filters.createdDateFrom || filters.createdDateTo) &&
                    renderPill(
                        `${dateFilterFormatter(filters.createdDateFrom).format("DD MMM'YY")} - ${dateFilterFormatter(filters.createdDateTo).format("DD MMM'YY")}`,
                        'createdDate'
                    )}

            </div>

            {/* RIGHT SIDE */}
            {onClear && <button className={styles.clearAll} onClick={onClear}>
                Clear All
            </button>
            }
        </div>
    );
};

export const DealInsightsDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // ui 
    const selectedTab = searchParams.get('riskStatus') || dealInsightsTabs[0].value;
    const searchQuery = searchParams.get('search') || '';
    const [showSliderFilter, setShowSliderFilter] = useState(false); // slider filter toggle
    const [showMyDeals, setShowMyDeals] = useState(false); // toggle for only user
    const [bulkFiltersLabels, setBulkFiltersLabels] = useState<Record<string, any>>(BulkFiltersDefaultValues);

    const user = useSelector((state: any) => get(state, 'users.me')); // user
    const dealSortBy = searchParams.get('sortBy') || dealTableHeaders[size(dealTableHeaders) - 1]?.value;
    const dealSortOrder = (searchParams.get('order') as SortOrder) || 'desc';

    // request state's
    const [requestDealFilters, setRequestDealFilters] = useState<DealsRequestPayload>(intialRequestFilters); // query Filter Request

    // query's
    const { data: dealRecords, hasNextPage: hasNextPageEvents, loadMore: loadMoreEvents, isFetching: isFetchingRecords, isFetchingNextPage: isFetchingNextPageEvents, refetch: allRecordsFetch, isRefetching: isRefetchingDealRecords, isLoading: isLoadingDealRecords, } = useDealsList(requestDealFilters);
    const { data: dealSummary, isLoading: isLoadingDealSummary } = useDealInsightsSummary(); // summary / insights
    const { data: filterOptions, isLoading: isLoadingFilterOptions } = useDealFilterOptions();// fetch the filter options.

    // Methods
    const listOfDeals = useMemo(() => { // list of deals new deals are appended only when data is fetched
        return flatMap(dealRecords?.pages || [], (page) => page.deals);
    }, [dealRecords]);

    useEffect(() => {  // initial if empty then set to default value
        const searchParams = new URLSearchParams(location.search);
        const isEmpty = !searchParams.toString();

        if (isEmpty) {
            const updatedQuery = {
               'riskStatus': dealInsightsTabs[0].value,
                ...{
                    sortBy: dealTableHeaders[size(dealTableHeaders) - 1]?.value,
                    order: 'desc',
                },
            };
            const url = buildUrl(location.pathname, { queryParams: updatedQuery });
            navigate(url);
        }
    }, [])

    useEffect(() => { // bulk filter label render
        if (!filterOptions || !location.search) { // if no filter options or no location search then set the bulk filters labels to empty
            setBulkFiltersLabels({});
            return;
        };

        const searchParams = new URLSearchParams(location.search);
        const allParams = Object.fromEntries(searchParams.entries());
        // bulk filters
        const bulkFilterKeys = Object.keys(omit(BulkFiltersDefaultValues, ['riskStatus']));
        const appliedBulkFilters = pickBy(allParams, (_, key) => bulkFilterKeys.includes(key));

        if (!isEmpty(appliedBulkFilters)) {
            setBulkFiltersLabels(() => {
                const filters: Record<string, any> = { ...appliedBulkFilters };
                if (filterOptions) {
                    // pipeline
                    if (appliedBulkFilters?.pipelineExternalId) {
                        const selectedPipeline = find(filterOptions?.pipelines, (pipeline) => pipeline.externalId === appliedBulkFilters?.pipelineExternalId);
                        filters['pipelineExternalId'] = selectedPipeline;

                        if (appliedBulkFilters?.stageExternalId) {
                            const stages = selectedPipeline?.stages;
                            const stageIds = appliedBulkFilters?.stageExternalId?.split(',') || [];
                            filters['stageExternalId'] = filter( stages, (stage) => includes(stageIds, stage?.externalId)) || [];
                        }
                    }
                    // owner
                    if (appliedBulkFilters?.ownerExternalId) {
                        const ownerIds = appliedBulkFilters?.ownerExternalId?.split(',') || [];
                        filters['ownerExternalId'] = filter( filterOptions?.owners, (i) => includes(ownerIds, i?.externalId)) || [];
                    }
                }
                return filters;
            })
        } else {
            setBulkFiltersLabels({});
        }
    }, [location.search, filterOptions]);


    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const allParams: Record<string, any> = Object.fromEntries(searchParams.entries());
        
        bulkFiltersMultiSelectKeys.forEach((key) => {
            if (allParams[key]) {
              allParams[key] = allParams[key].split(',');
            }
        });

        if (size(allParams) > 0) {
            const { riskStatus, ...params } = allParams; // all the params except riskStatus

            const numberFields = [
                'minAmount',
                'maxAmount',
                'closeDateFrom',
                'closeDateTo',
                'lastActivityFrom',
                'lastActivityTo',
                'createdDateFrom',
                'createdDateTo',
            ]; // number fields to convert to number

            const rest: Record<string, any> = params;
            numberFields.forEach((key) => {
                if (rest[key]) {
                    rest[key] = toNumber(rest[key]);
                }
            });

            setRequestDealFilters(() => ({
                ...rest, // all the rest params
                ...(riskStatus && riskStatus !== dealInsightsTabs[0].value && { 'riskStatus': snakeCase(riskStatus) }),
                page: 1,
                size: 20,
            }));
        } else {
            setRequestDealFilters(() => ({
                page: 1,
                size: 20,
            }));
        }
    }, [location.search])

    // Methods
    // query parms
    const updateQueryParams = (key: string, value: any) => { // add query params
        const searchParams = new URLSearchParams(location.search);
        const existingParams = Object.fromEntries(searchParams.entries());
        const updatedQuery = {
            ...existingParams,
            [key]: value
        };
        const url = buildUrl(location.pathname, { queryParams: updatedQuery });
        navigate(url);
    }

    const removeQueryParams = (keys: string | string[]) => { // remove query params
        const searchParams = new URLSearchParams(location.search);
        forEach(Array.isArray(keys) ? keys : [keys], (key) => {
            searchParams.delete(key); // remove
        });
        const queryString = searchParams.toString();
        const url = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(url);
    }

    const checkBulkFiltersHasValues = (filters: Record<string, any>) => some(omit(filters, ['riskStatus', 'hasDealRoom']),
        (value) => value !== null && value !== undefined && value !== ''
    );

    const handleTabChange = (tab: string) => {
        if (tab === selectedTab) return;
        updateQueryParams('riskStatus', tab);
    }

    const handleSearchQuery = (query: string | null) => {
        if (query && size(trim(query)) > 0) {
            updateQueryParams('search', query);
        } else {
            removeQueryParams('search');
        }
    }

    const handleMyDealsToggle = () => {
        if (showMyDeals) { // remove owner filter
            removeQueryParams('ownerExternalId');
        } else { // add owner filter
            const userId = get(user, 'id');
            updateQueryParams('ownerExternalId', userId);
        }

        setShowMyDeals(!showMyDeals); // toggle
    };

    const handleRemoveBulkFilters = (key: string, value?: string) => { // remove particular bulk filter
        const searchParams = new URLSearchParams(location.search);

        if (bulkFiltersMultiSelectKeys.includes(key) ) { // multi select keys
          const current = searchParams.get(key)?.split(',') || [];
          const updated = current.filter((i) => i !== value);
      
          if (updated.length > 0) {
            searchParams.set(key, updated.join(','));
          } else {
            searchParams.delete(key);
          }

          navigate( `${location.pathname}?${searchParams.toString()}` );      
          return;
        }

        if (key === 'pipelineExternalId') { // pipelineExternalId
            removeQueryParams(['pipelineExternalId', 'stageExternalId']);
        } else if (key === 'closeDate') { // closeDate
            removeQueryParams(['closeDateFrom', 'closeDateTo']);
        } else if (key === 'lastActivity') { // lastActivity
            removeQueryParams(['lastActivityFrom', 'lastActivityTo']);
        } else if (key === 'createdDate') { // createdDate
            removeQueryParams(['createdDateFrom', 'createdDateTo']);
        } else if (key === 'amount') { // amount
            removeQueryParams(['minAmount', 'maxAmount']);
        } else {
            removeQueryParams(key);
        }
    };

    const handleClearBulkFilters = () => { // clear bulk filter
        const searchParams = new URLSearchParams(location.search);
        const bulkFilterKeys = Object.keys(omit(BulkFiltersDefaultValues, ['riskStatus']));
        bulkFilterKeys.forEach((key) => {
            searchParams.delete(key); // remove key in route
        });
        const queryString = searchParams.toString();
        const url = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(url);
    }

    const handleSort = (val: string, key: string) => {
        const searchParams = new URLSearchParams(location.search);
        const existingParams = Object.fromEntries(searchParams.entries());
        const updatedQuery = {
            ...existingParams,
            'sortBy': key,
            'order': val,
        };
        const url = buildUrl(location.pathname, { queryParams: updatedQuery });
        navigate(url);
    }
    
    // Render
    return (
        <div className={cx(styles.dashboardContainer)}>
            <div className={styles.contentContainer}>
                {/* LEFT GRID: Primary Workspace (Tabs, Table) */}
                <div className={styles.leftPane}>
                    <div className={styles.header}>
                        <h6 className={styles.title}>deal insights</h6>
                        <p className={styles.subText}>a snapshot of all your deals in one place</p>
                    </div>

                    <div className={styles.tabFilterWrapper}>
                        <TabPanel
                            listOfTabs={dealInsightsTabs}
                            selected={selectedTab}
                            onChange={(val) => handleTabChange(val)}
                        />

                        <Actions.Button
                            width={2}
                            className={styles.filterButton}
                            onClick={() => setShowSliderFilter(!showSliderFilter)}
                            title="Filters"
                            icon={cx('paperflite-dropdown', styles.icon)}
                        />
                    </div>

                    {/* Fitler Pills */}
                    {checkBulkFiltersHasValues(bulkFiltersLabels) && (
                        <FilterPills
                            filters={bulkFiltersLabels}
                            onRemove={handleRemoveBulkFilters}
                            onClear={handleClearBulkFilters}
                        />
                    )}

                    {/* Deals Wrapper: search + toggle controls above the deal table */}
                    <div className={styles.dealsWrapper}>
                        <div className={styles.dealsControls}>
                            <div className={styles.searchBar}>
                                <SearchBar
                                    query={searchQuery}
                                    onSearch={(query: string | null) => handleSearchQuery(query)}
                                />
                            </div>
                            {/* <div className={cx(styles.mydeals)}>
                                <SwitchButton
                                    id="show-my-deals"
                                    checked={showMyDeals}
                                    handleCheck={handleMyDealsToggle}
                                    label="Show only my deals"
                                    className={styles.switchToggle}
                                />
                            </div> */}
                        </div>

                        {/* Deal Table */}
                        <div className={styles.tableWrapper}>
                            <Row className={cx(styles.tableHeaderRow)}>
                                {map(dealTableHeaders, (header) => (
                                    <TableHeader
                                        key={header.label}
                                        col={header.width}
                                        value={header.label}
                                        onClick={(val) => handleSort(val, header.value)}
                                        sort={header.sort}
                                        sortOrder={dealSortBy === header.value ? dealSortOrder : ''}
                                    />
                                ))}
                            </Row>
                            <div className={styles.tableBody}>
                                {isLoadingDealRecords || isRefetchingDealRecords ? <div className={styles.tableBodyWrapper}>
                                    {times(4, (index) => (
                                        <RowPlaceholder key={index} />
                                    ))} </div>
                                    : <>{size(listOfDeals) > 0 ? (
                                        <InfiniteScroll
                                            loading={isFetchingRecords}
                                            hasMore={hasNextPageEvents}
                                            loadMore={loadMoreEvents}
                                            loader={() => ( 
                                                <> 
                                                    <RowPlaceholder />
                                                    <RowPlaceholder />
                                                </>
                                            )}
                                            className={styles.tableBodyWrapper}
                                        >
                                            {map(listOfDeals, (deal: Deal, index) => <NavLink 
                                                key={deal.id}
                                                id={deal.id}  
                                                to={`/deal-insights/${deal.id}`}>
                                                <Row className={cx(styles.tableRow)}>
                                                    <TableCell col={dealTableHeaders[0].width} type="deal_name" value={deal.dealName} classnames={styles.name} />
                                                    <TableCell col={dealTableHeaders[1].width} type="amount" value={deal.amount} />
                                                    <TableCell col={dealTableHeaders[2].width} type="health_score" value={deal.predictionScore} predictionTrend={deal.predictionTrend} />
                                                    <TableCell col={dealTableHeaders[2].width} type="owner" value={deal.ownerName} />
                                                    <TableCell col={dealTableHeaders[3].width} type="external_updated_at" value={deal.lastActivity} classnames={styles.arrow} showArrow={true} />
                                                </Row>
                                                </NavLink>
                                            )}
                                        </InfiniteScroll>
                                    ) : (
                                        <div className={styles.noDeals}>
                                            <NoResultsFound msg="No results found!" src={emptyDeals} />
                                        </div>
                                    )} </>
                                }
                            </div>
                        </div>

                        {/* Bottom fade overlay */}
                        <div className={styles.bottomOverlay} />
                    </div>
                </div>

                {/* RIGHT GRID: AI Insights & Metrics */}
                <div className={styles.rightPane}>
                    <div className={styles.content}>
                    {/* Deal Agent */}
                    <div className={styles.dealAgent}>
                        <DealAgent/>
                    </div>

                    {/* Recent Activities */}
                    <DealActivity isDetailedView={false} classnames={styles.recentActivities} title={'recent activities'} />

                    {/* Insights */}
                    <div className={styles.insights}>
                        <p className={styles.title}>insights</p>
                        <div className={styles.content}> 
                            { isLoadingDealSummary ?
                                <>
                                   {times(4, (index) => (
                                    <InsightPlaceholder key={index} />
                                   ))}
                                </>
                                : <>
                                    {size(dealSummary) > 0 ?
                                        <>
                                            {map(omit(dealSummary, ['thisMonthActivityChangePct']), (value: number, key: string) => {
                                                return <div className={styles.item}>
                                                    <p className={styles.title}>{inSightsLabel[key]}</p>
                                                    <p className={styles.value}>
                                                        { (key === 'valueByRevenue' || key === 'avgDealSize') ? formatCurrencyToUSD(value) : `${round(value)}` }</p>
                                                </div>
                                            })}
                                        </>
                                        : <div className={styles.noInsights}>
                                            <NoResultsFound msg="No insights found!" src={emptyInsights} />
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            {/* Filter Slider */}
            <FiltersSlider
                isOpen={showSliderFilter}
                onClose={() => setShowSliderFilter(false)}
                filterOptions={filterOptions} />
        </div>
    );
};

export default withErrorBoundary(DealInsightsDashboard);

